import {
    ChessGame,
    type FromChessComOptions,
    type Opening,
} from "../models/chess-game-model";
import { type ChessComRawGame } from "../features/import/models/chess-com-models";
import {
    ClassifyOpeningUtil,
    type OpeningMatch,
} from "../utils/classify-opening-util";
import { invariant, sleep } from "@banjoanton/utils";
import { OpeningUtil } from "../utils/opening-util";
export const OPENING_INDEX_VERSION = "2025.08";
export const MAX_PLY = 25;

export type OpeningIndex = Map<string, OpeningMatch>;

export type OpeningPayload = {
    version: string;
    format: "fen-index@1";
    entries: Record<string, OpeningMatch>;
};

export type EnrichGamesWithOpeningsProps = {
    allGames: ChessComRawGame[];
    playerName: string;
    collectionId: string;
    onProgress?: (progress: number) => void;
    openingIndex?: OpeningIndex;
};

let cachePromise: Promise<OpeningIndex> | null = null;

const loadOpeningIndex = async (): Promise<OpeningIndex> => {
    if (cachePromise) {
        return cachePromise;
    }
    cachePromise = (async () => {
        const res = await fetch("/opening-index.json", {
            cache: "force-cache",
        });
        const data = (await res.json()) as OpeningPayload;
        return new Map(Object.entries(data.entries));
    })();
    return cachePromise;
};

const getOpening = async (pgn: string) => {
    const openingIndex = await loadOpeningIndex();
    const match = ClassifyOpeningUtil.fromPGN(pgn, MAX_PLY, openingIndex);
    invariant(match, "match must exist");
    const opening = OpeningUtil.getFormattedOpening(
        match,
        OPENING_INDEX_VERSION
    );

    return opening;
};

const enrichGamesWithOpenings = async ({
    allGames,
    playerName,
    collectionId,
    onProgress,
    openingIndex,
}: EnrichGamesWithOpeningsProps) => {
    if (!openingIndex) {
        openingIndex = await loadOpeningIndex();
    }

    let completed = 0;
    const totalLength = allGames.length;
    const batchSize = 100;
    const results: ChessGame[] = [];

    onProgress?.(0);

    for (let i = 0; i < allGames.length; i += batchSize) {
        const batch = allGames.slice(i, i + batchSize);

        for (const game of batch) {
            const match = ClassifyOpeningUtil.fromPGN(
                game.pgn,
                MAX_PLY,
                openingIndex
            );

            let opening: Opening;
            if (match) {
                opening = OpeningUtil.getFormattedOpening(
                    match,
                    OPENING_INDEX_VERSION
                );
            } else {
                opening = OpeningUtil.getUnknownOpening();
            }

            const options: FromChessComOptions = {
                game,
                username: playerName,
                collectionId,
                opening,
            };

            results.push(ChessGame.fromChessCom(options));
            completed++;
        }

        onProgress?.(completed / totalLength);

        // Yield to the event loop so React can update
        await sleep(0);
    }

    return results;
};

export const OpeningService = {
    loadOpeningIndex,
    enrichGamesWithOpenings,
    getOpening,
};
